/**
 * Centralized help text constants.
 * Keeps prose out of components for easier maintenance and consistency.
 */

// ─── End User Status ────────────────────────────────────────────────────────

export const END_USER_STATUS_HELP: Record<string, string> = {
  invited: "Pickaxe account created with credentials sent. User hasn't logged in yet.",
  verified: "Pickaxe email verified but hasn't completed their first session.",
  active: "Active in both Pickaxe and Studio Manager with an assigned access group.",
  suspended: "Pickaxe access temporarily revoked — account and history preserved. Can be unsuspended at any time.",
  inactive: "Not found in Pickaxe during last sync. May need to be reactivated with an access group.",
  archived: "Permanently deleted from Pickaxe. Record kept in Studio Manager — can be restored as a new account.",
  contact: "Captured via a workshop registration; has no studio login.",
};

// ─── Organization Status ────────────────────────────────────────────────────

export const ORG_STATUS_HELP: Record<string, string> = {
  active: "Organization is active and all studios are accessible.",
  suspended: "Organization is suspended. All studios and users are locked out.",
};

// ─── Studio Status ──────────────────────────────────────────────────────────

export const STUDIO_STATUS_HELP: Record<string, string> = {
  active: "Studio is live and end users can access it.",
  suspended: "Studio is suspended. End users cannot access it.",
};

// ─── Invite Link Status ─────────────────────────────────────────────────────

export const INVITE_LINK_STATUS_HELP: Record<string, string> = {
  active: "Link is active and can be used to create accounts.",
  revoked: "Link has been manually deactivated.",
  exhausted: "Link has reached its maximum number of uses.",
  expired: "Link has passed its expiration date.",
};

// ─── Invite Link Scope ──────────────────────────────────────────────────────

export const INVITE_SCOPE_HELP: Record<string, string> = {
  studio:
    "Anyone with the link can create an account. No access group or roster is pre-assigned.",
  access_group: "Anyone with the link gets the selected access group automatically.",
  group:
    "Anyone with the link is added to the selected roster with its default access group.",
  individual:
    "Only the specified email can use this link. Best for named invitations.",
};

// ─── Platform Roles ─────────────────────────────────────────────────────────

export const PLATFORM_ROLE_HELP: Record<string, string> = {
  PLATFORM_OWNER:
    "Full platform access. Can manage all organizations, studios, and platform team members.",
  PLATFORM_ADMIN:
    "Full platform access to organizations and studios. Cannot manage other platform team members.",
};

// ─── Field Hints ────────────────────────────────────────────────────────────

export const FIELD_HINTS = {
  // Org fields
  orgSlug:
    "URL-safe identifier for this organization. Used in internal references.",

  // Studio fields
  apiKey:
    "Your Pickaxe Studio API key. Found in Pickaxe Studio Settings. Stored encrypted.",
  workspaceUrl:
    "The Pickaxe workspace URL for this studio (e.g. https://app.pickaxeproject.com/workspace/...).",
  baseDomainUrl:
    "The base Pickaxe domain for agent embeds (e.g. https://embed.pickaxeproject.com).",
  customDomainUrl:
    "Optional custom domain if configured in Pickaxe (e.g. https://ai.clientdomain.com).",
  loginUrl:
    "The login page URL for end users. If set, included in invitation emails.",

  // Invite link fields
  maxUses:
    "Maximum number of times this link can be used. Leave at 0 for unlimited.",
  expiresIn:
    "How long until this link expires. After expiry, the link cannot be redeemed.",

  // Group fields
  groupContact: "Optional point of contact for this roster.",
  defaultTiers:
    "Access groups automatically assigned to users added to this roster.",

  // User fields
  userNotes:
    "Internal notes about this user. Synced to Pickaxe as the user summary.",
  pickaxeData:
    "Live data from Pickaxe. Usage counts and access group assignments are managed by Pickaxe.",

  // Document fields
  documentRawContent:
    "Paste text content directly. This will be indexed as a knowledge source for agents.",
  documentWebsite:
    "Provide a URL to scrape. The page content will be indexed as a knowledge source.",
  documentConnect:
    "Agent-scoped connections make the document available to all users. User-scoped connections provide personalized context for a specific user.",

  // Sync
  agentSync:
    "Pulls the latest user list and usage data from Pickaxe into Studio Manager.",
} as const;

// ─── Empty States ───────────────────────────────────────────────────────────

export const EMPTY_STATES = {
  groups:
    "Rosters let you organize end users and assign default access groups. Create a roster to get started.",
  inviteLinks:
    "Invite links let end users self-enroll into the studio. Generate a link to share.",
  noTiers:
    "No access groups available. Sync from Pickaxe first to load available access groups.",
  documents:
    "Documents are knowledge sources for your AI agents. Add text content or a website URL to get started.",
} as const;
